import { useNavigate, useParams } from "react-router";
import { Trash2 } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import { eftHashPath } from "@cle/constants/routes";
import ConfirmationModal from "@cle/components/ConfirmationModal";
import RoundedCard from "@cle/components/RoundedCard";
import {
  useDeleteTeaching,
  useTeachingDocument,
  useTeachingDocumentFile,
} from "@/hooks/useTeachings";
import { formatDanishLongDate } from "@/util/date";
import EvidenceStatusBadge from "../../components/EvidenceStatusBadge";
import ManualReviewInfoModal from "./components/ManualReviewInfoModal";
import SecretariatMessageCard from "./components/SecretariatMessageCard";
import SupplementaryDocumentationCard from "./components/SupplementaryDocumentationCard";
import TeachingReviewNotice from "./components/TeachingReviewNotice";
import UploadedDocumentPreviewCard from "./components/UploadedDocumentPreviewCard";
import { formatLessons } from "../../utils/lessonLimits";
import { shouldShowSupplementaryCard } from "../../utils/supplementaryDocumentation";

const MISSING_VALUE = "-";

const TeachingDocumentView = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: teaching,
    isError: isTeachingError,
    isLoading: isTeachingLoading,
  } = useTeachingDocument(id);
  const {
    data: documentFile,
    isError: isDocumentFileError,
    isLoading: isDocumentFileLoading,
  } = useTeachingDocumentFile(id);
  const deleteTeachingMutation = useDeleteTeaching();
  const teachingTitle = teaching?.fileName ?? "Undervisning.pdf";
  const isReported = teaching?.isReported ?? false;
  const isDeleteDisabled =
    id.length === 0 || deleteTeachingMutation.isPending || isReported;

  const handleDeleteTeaching = (closeConfirmation: () => void) => {
    deleteTeachingMutation.mutate(id, {
      onSuccess: () => {
        closeConfirmation();
        void navigate(eftHashPath("teachings"));
      },
    });
  };

  return (
    <div className="cle-course-certificate-document">
      {isTeachingError ? (
        <div className="alert alert-warning" role="alert">
          Undervisningens oplysninger kunne ikke indlæses lige nu.
        </div>
      ) : null}
      {deleteTeachingMutation.isError ? (
        <div className="alert alert-warning" role="alert">
          Undervisningen kunne ikke slettes lige nu. Prøv igen.
        </div>
      ) : null}
      {teaching ? (
        <TeachingReviewNotice
          isReported={isReported}
          status={teaching.status}
        />
      ) : null}

      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <UploadedDocumentPreviewCard
              certificateTitle={teachingTitle}
              fileBlob={documentFile}
              isError={isDocumentFileError}
              isLoading={isDocumentFileLoading}
            />
            {/* Samme placering som på kursusbeviset: under dokumentet, som
                bilagene understøtter (ADV-359). */}
            {teaching && shouldShowSupplementaryCard(teaching) ? (
              <SupplementaryDocumentationCard
                evidence={teaching}
                kind="undervisning"
              />
            ) : null}
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <RoundedCard
              className="cle-course-certificate-document__side-card"
              header="Information"
              action={
                <>
                  <ManualReviewInfoModal
                    documentType="undervisning"
                    isReported={isReported}
                  />
                  <ConfirmationModal
                    title="Slet undervisning"
                    description="Er du sikker på, at du vil slette undervisningen? Handlingen kan ikke fortrydes."
                    confirmLabel="Slet"
                    confirmVariant="destructive"
                    confirmButtonId="cle-confirm-delete-teaching"
                    cancelButtonId="cle-cancel-delete-teaching"
                    isConfirming={deleteTeachingMutation.isPending}
                    isConfirmDisabled={isDeleteDisabled}
                    onConfirm={({ close }) => handleDeleteTeaching(close)}
                    trigger={
                      <CleButton
                        id="cle-delete-teaching"
                        variant="destructive"
                        size="sm"
                        disabled={isDeleteDisabled}
                      >
                        <Trash2 size={16} aria-hidden strokeWidth={1.75} />
                        {deleteTeachingMutation.isPending
                          ? "Sletter..."
                          : "Slet"}
                      </CleButton>
                    }
                  />
                </>
              }
            >
              <dl className="cle-course-certificate-document__info-list mb-0">
                <dt>Dokument</dt>
                <dd>{teachingTitle}</dd>

                <dt>Type</dt>
                <dd>Undervisning</dd>

                <dt>Status</dt>
                <dd>
                  {teaching ? (
                    <EvidenceStatusBadge
                      reported={isReported}
                      status={teaching.status}
                    />
                  ) : (
                    MISSING_VALUE
                  )}
                </dd>

                <dt>Antal lektioner</dt>
                <dd>
                  {isTeachingLoading
                    ? "Indlæser"
                    : formatLessons(teaching?.numberOfLessons, MISSING_VALUE)}
                </dd>

                <dt>Uploadet den</dt>
                <dd>
                  {formatDanishLongDate(teaching?.createdAt, MISSING_VALUE)}
                </dd>
              </dl>
            </RoundedCard>
            <SecretariatMessageCard subjectId={id} isReported={isReported} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeachingDocumentView;
